/**
 * Admin Badge Analytics Dashboard
 * Comprehensive analytics for candidate badge system
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Award, TrendingUp, Users, Target, BarChart3, 
  Calendar, ArrowUp, ArrowDown, Minus, Filter,
  PieChart, Activity, Clock
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette for charts
const COLORS = ['#f59e0b', '#10b981', '#6366f1', '#ec4899', '#8b5cf6', '#14b8a6', '#f43f5e'];

const AdminBadgeAnalytics = () => {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryAnalytics, setCategoryAnalytics] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [trendDays, setTrendDays] = useState('30');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverview();
    fetchTrends(30);
    fetchConversion();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryAnalytics(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchOverview = async () => {
    try {
      const response = await fetch(`${API_URL}/api/candidate-badges/admin/analytics/overview`);
      const data = await response.json();
      if (data.success) {
        setOverview(data);
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async (days) => {
    try {
      const response = await fetch(`${API_URL}/api/candidate-badges/admin/analytics/trends?days=${days}`);
      const data = await response.json();
      if (data.success) {
        setTrends(data);
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
    }
  };

  const fetchCategoryAnalytics = async (category) => {
    try {
      const response = await fetch(`${API_URL}/api/candidate-badges/admin/analytics/category/${category}`);
      const data = await response.json();
      if (data.success) {
        setCategoryAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching category analytics:', error);
    }
  };

  const fetchConversion = async () => {
    try {
      const response = await fetch(`${API_URL}/api/candidate-badges/admin/analytics/conversion`);
      const data = await response.json();
      if (data.success) {
        setConversion(data);
      }
    } catch (error) {
      console.error('Error fetching conversion:', error);
    }
  };

  const handleTrendDaysChange = (days) => {
    setTrendDays(days);
    fetchTrends(parseInt(days));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-gray-200 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const categories = overview?.badges_by_category?.map(c => c.category) || [];

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-badge-analytics">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-100 rounded-xl">
            <Award className="w-8 h-8 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Badge Analytics Dashboard</h1>
            <p className="text-gray-500">Monitor badge performance, trends, and conversion</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Award className="w-8 h-8 opacity-80" />
                <div className="text-right">
                  <p className="text-3xl font-bold">{overview?.overview?.total_badges_awarded || 0}</p>
                  <p className="text-xs opacity-80">Total Awarded</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Users className="w-6 h-6 text-blue-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{overview?.overview?.unique_badge_holders || 0}</p>
                  <p className="text-xs text-gray-500">Badge Holders</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Calendar className="w-6 h-6 text-emerald-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{overview?.overview?.badges_this_week || 0}</p>
                  <p className="text-xs text-gray-500">This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <TrendingUp className="w-6 h-6 text-purple-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{overview?.overview?.badges_this_month || 0}</p>
                  <p className="text-xs text-gray-500">This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Target className="w-6 h-6 text-rose-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{overview?.overview?.total_badge_types || 0}</p>
                  <p className="text-xs text-gray-500">Badge Types</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <PieChart className="w-6 h-6 text-indigo-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{overview?.overview?.total_categories || 0}</p>
                  <p className="text-xs text-gray-500">Categories</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Activity className="w-6 h-6 text-teal-500" />
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{conversion?.conversion_rate || 0}%</p>
                  <p className="text-xs text-gray-500">Conversion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="conversion">Conversion</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Badges by Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-amber-500" />
                    Top Badges
                  </CardTitle>
                  <CardDescription>Most awarded badge types</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={overview?.badges_by_type?.slice(0, 8) || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Badges by Category */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-purple-500" />
                    By Category
                  </CardTitle>
                  <CardDescription>Badge distribution across categories</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={overview?.badges_by_category || []}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ category, percent }) => `${category.split('-')[0]} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {overview?.badges_by_category?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-500" />
                  Recent Badge Awards
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {overview?.recent_awards?.map((award, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{award.badge_icon}</span>
                        <div>
                          <p className="font-medium text-gray-900">{award.badge_name}</p>
                          <p className="text-sm text-gray-500">{award.candidate_display}</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(award.awarded_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                      Badge Award Trends
                    </CardTitle>
                    <CardDescription>Daily badge awards over time</CardDescription>
                  </div>
                  <Select value={trendDays} onValueChange={handleTrendDaysChange}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="14">Last 14 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="60">Last 60 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trends?.daily_trend || []}>
                      <defs>
                        <linearGradient id="colorBadges" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11 }}
                        tickFormatter={(value) => value.slice(5)}
                      />
                      <YAxis />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="count" 
                        stroke="#f59e0b" 
                        fillOpacity={1} 
                        fill="url(#colorBadges)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 text-center">
                  <Badge variant="outline" className="text-lg px-4 py-2">
                    Total: {trends?.total_in_period || 0} badges in {trendDays} days
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Filter className="w-5 h-5 text-indigo-500" />
                      Category Analytics
                    </CardTitle>
                    <CardDescription>Detailed breakdown by category</CardDescription>
                  </div>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {categoryAnalytics ? (
                  <div className="space-y-6">
                    {/* Category Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-amber-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-600">{categoryAnalytics.total_badges_awarded}</p>
                        <p className="text-sm text-amber-700">Badges Awarded</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{categoryAnalytics.badge_types_count}</p>
                        <p className="text-sm text-blue-700">Badge Types</p>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-600">{categoryAnalytics.top_earners?.length || 0}</p>
                        <p className="text-sm text-emerald-700">Top Earners</p>
                      </div>
                    </div>

                    {/* Badges in Category */}
                    <div>
                      <h4 className="font-semibold mb-3">Badges</h4>
                      <div className="space-y-2">
                        {categoryAnalytics.badges?.map((badge) => (
                          <div key={badge.badge_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{badge.icon}</span>
                              <div>
                                <p className="font-medium">{badge.name}</p>
                                <p className="text-xs text-gray-500">+{Math.round((badge.search_boost - 1) * 100)}% boost</p>
                              </div>
                            </div>
                            <Badge variant="secondary">{badge.count} awarded</Badge>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top Earners */}
                    {categoryAnalytics.top_earners?.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3">Top Badge Earners</h4>
                        <div className="space-y-2">
                          {categoryAnalytics.top_earners?.map((earner, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                  idx === 0 ? 'bg-amber-100 text-amber-600' :
                                  idx === 1 ? 'bg-gray-200 text-gray-600' :
                                  idx === 2 ? 'bg-orange-100 text-orange-600' :
                                  'bg-gray-100 text-gray-500'
                                }`}>
                                  {idx + 1}
                                </div>
                                <div>
                                  <p className="font-medium">{earner.display_name}</p>
                                  <div className="flex gap-1">
                                    {earner.badge_icons?.map((icon, i) => (
                                      <span key={i}>{icon}</span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <Badge>{earner.badge_count} badges</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <PieChart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Select a category to view detailed analytics</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Conversion Tab */}
          <TabsContent value="conversion" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Conversion Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-4xl font-bold text-amber-600">{conversion?.conversion_rate || 0}%</p>
                    <p className="text-sm text-amber-700">Service → Badge Rate</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-xl font-bold">{conversion?.total_service_purchases || 0}</p>
                      <p className="text-xs text-gray-500">Purchases</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-xl font-bold">{conversion?.total_badges_awarded || 0}</p>
                      <p className="text-xs text-gray-500">Badges</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Badge Conversion by Type</CardTitle>
                  <CardDescription>Which badges are being earned most</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {conversion?.badge_conversion?.slice(0, 10).map((badge) => (
                      <div key={badge.badge_id} className="flex items-center gap-4">
                        <span className="text-2xl w-8">{badge.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{badge.name}</span>
                            <span className="text-sm text-gray-500">{badge.total_awarded}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-amber-500 rounded-full"
                              style={{ 
                                width: `${Math.min((badge.total_awarded / Math.max(...conversion?.badge_conversion?.map(b => b.total_awarded) || [1])) * 100, 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminBadgeAnalytics;
